import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { dxSolutionCopy as c, type Bi } from '../../data/digitalExperienceSolution';
import { FitScale } from './FitScale';
import { SolutionTopCanvas, ExperiencesMobile, ChannelsMobile } from './solution/SolutionTop';
import { PlatformCard, FoundationRow, ServicesStrip } from './solution/SolutionFoundation';
const EASE = [0.23, 1, 0.32, 1] as const;

// Thin indigo convergence lines into the platform card (desktop only).
function ConvergeLines() {
  return <svg className="hidden md:block w-full h-[56px]" viewBox="0 0 1000 56" preserveAspectRatio="none" aria-hidden="true" data-id="element-3326" data-mp="ppojjpnrgc6l">
      <path d="M430 0 C 440 30, 470 40, 480 56" fill="none" stroke="#8C87F2" strokeWidth={1.2} vectorEffect="non-scaling-stroke" data-id="element-3327" data-mp="lpece9o8ffq5" />
      <path d="M560 0 C 555 30, 530 40, 520 56" fill="none" stroke="#8C87F2" strokeWidth={1.2} vectorEffect="non-scaling-stroke" data-id="element-3328" data-mp="2k365x9cfj301" />
      <path d="M900 0 C 880 36, 720 44, 640 56" fill="none" stroke="#B4B0F5" strokeWidth={1.2} vectorEffect="non-scaling-stroke" data-id="element-3329" data-mp="30mbbnantp3vz" />
    </svg>;
}
function DescendLines() {
  const xs = [150, 270, 390, 500, 610, 730, 850];
  return <svg className="hidden xl:block w-full h-[40px]" viewBox="0 0 1000 40" preserveAspectRatio="none" aria-hidden="true" data-id="element-3330" data-mp="2rmokdhot5egn">
      {xs.map(x => <path key={x} d={`M${500 + (x - 500) * 0.55} 0 C ${500 + (x - 500) * 0.6} 20, ${x} 18, ${x} 40`} fill="none" stroke="#B4B0F5" strokeWidth={1.1} vectorEffect="non-scaling-stroke" data-id="element-3331" data-mp="2vp1oy2rtj6bu" />)}
    </svg>;
}
export function DigitalSolution() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <div className="relative max-w-[1680px] mx-auto" data-id="element-3332" data-mp="3el17kmywoxir">
      <div className="grid grid-cols-1 xl:grid-cols-[29fr_71fr] gap-10 xl:gap-8 items-start" data-id="element-3333" data-mp="3ejq4yqh75vt8">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} className="min-w-0" data-id="element-3334" data-mp="64g75ukcbcr9">
          <p className="font-sans text-[13px] md:text-[14px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-3335" data-mp="gsfv89d46ign">{pick(c.eyebrow)}</p>
          <h2 className="font-serif text-[2.6rem] sm:text-[3.4rem] 2xl:text-[4.2rem] leading-[1.02] tracking-tight mt-5" data-id="element-3336" data-mp="bue9ths7u720">
            {c.navy.map(l => <span key={l.fr} className="block text-[#121A3A]" data-id="element-3337" data-mp="1uncz9g6nfyr">{pick(l)}</span>)}
            <span className="block text-[#4F46E5]" data-id="element-3338" data-mp="3t8t4lfkzkbe1">{pick(c.indigo)}</span>
          </h2>
          <p className="font-sans text-[16px] md:text-[17px] text-[#4A5268] leading-relaxed mt-6 max-w-[560px]" data-id="element-3339" data-mp="3t6ynydgqz5fr">{pick(c.body)}</p>
          <ul className="mt-6 space-y-3" data-id="element-3340" data-mp="3k8mv04servk5">
            {c.benefits.map(b => <li key={b.fr} className="flex items-center gap-3" data-id="element-3341" data-mp="3f8qtqdb3ii8a">
                <span className="w-7 h-7 rounded-full bg-[#EEEDFD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-3342" data-mp="3fb4m2eee5gc5">
                  <CheckIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2.4} data-id="element-3343" data-mp="3068v7043r3tj" />
                </span>
                <span className="font-sans text-[15.5px] text-[#121A3A]" data-id="element-3344" data-mp="354toefkxsn8h">{pick(b)}</span>
              </li>)}
          </ul>
        </motion.div>

        <div className="hidden md:block min-w-0" data-id="element-3345" data-mp="3a4y0zuiq4xtf">
          <FitScale width={1240} height={470} data-id="element-3346" data-mp="3f3st5n2kzyq7"><SolutionTopCanvas data-id="element-3347" data-mp="3f2r9iu7i23wv" /></FitScale>
        </div>
        <div className="md:hidden" data-id="element-3348" data-mp="2hg0xixj695kq"><ExperiencesMobile data-id="element-3349" data-mp="2hgk3rgzgm5ix" /></div>
      </div>

      <div className="mt-8 md:mt-0 xl:pl-[14%]" data-id="element-3350" data-mp="2hdo6zvzclmw2">
        <ConvergeLines data-id="element-3351" data-mp="2mfn9z4r6ehsh" />
        <PlatformCard data-id="element-3352" data-mp="2meverv1hqm2q" />
        <div className="mt-5 md:mt-0" data-id="element-3353" data-mp="2mc959pv1hwhv"><FoundationRow data-id="element-3354" data-mp="2rd59k4jazknt" /></div>
      </div>

      <div className="md:hidden mt-6" data-id="element-3355" data-mp="2rcdp9pv5p9cu"><ChannelsMobile data-id="element-3356" data-mp="2w4ewgxdgw9w0" /></div>

      <div className="mt-6 xl:mt-0" data-id="element-3357" data-mp="2w4na4rhezj89">
        <DescendLines data-id="element-3358" data-mp="2w5q2l3udxtqa" />
        <ServicesStrip data-id="element-3359" data-mp="2w683idrzsw4j" />
      </div>

      <p className="mt-10 text-center font-serif text-[1.8rem] sm:text-[2.3rem] leading-tight tracking-tight" data-id="element-3360" data-mp="1ypsxwpeokx9a">
        <span className="text-[#121A3A]" data-id="element-3361" data-mp="23mt5j4r6i82i">{pick(c.finalNavy)}</span>{' '}
        <span className="text-[#4F46E5]" data-id="element-3362" data-mp="97cj75jsp0k8">{pick(c.finalIndigo)}</span>
      </p>
    </div>;
}